/**
 * How the picture is arranged: who is big, who is small, who is not shown.
 *
 * A two-person therapy session is not a meeting of twelve, so this is short
 * on purpose. What a therapist actually wants to change is whether they are
 * looking at both faces at once or at their patient's alone — and, for some,
 * whether they can see themselves at all. Watching your own face for fifty
 * minutes is a known distraction, and the reason Zoom eventually shipped a
 * way to turn it off.
 */

export type MeetingLayoutId = "gallery" | "speaker" | "solo";

export const MEETING_LAYOUTS: readonly MeetingLayoutId[] = [
    "gallery",
    "speaker",
    "solo",
];

/** Where the choice is remembered — per browser, never sent anywhere. */
export const LAYOUT_STORAGE_KEY = "alchely.meeting.layout";

/** The little a layout decision needs to know about a track. */
export type StageTrack = {
    isLocal: boolean;
    isScreenShare: boolean;
    /** Whether this is the person talking right now. */
    isSpeaking?: boolean;
    /** Whether somebody asked for this one to hold the stage, by hand. */
    isPinned?: boolean;
};

export type StagePlan<T> = {
    /** Everything at equal size, or null when one track is on the stage. */
    grid: T[] | null;
    /** The one track filling the stage, or null in the gallery. */
    stage: T | null;
    /** The small tiles over the stage: everything that is not on it. */
    rail: T[];
};

export function isMeetingLayoutId(value: unknown): value is MeetingLayoutId {
    return (
        typeof value === "string" &&
        (MEETING_LAYOUTS as readonly string[]).includes(value)
    );
}

/**
 * Where a room starts before anybody has chosen anything.
 *
 * On a laptop the grid is the kinder default: two faces at the same size is
 * the arrangement a conversation between equals actually has. A phone held in
 * portrait cannot draw that — two tiles stacked down a 390px column are two
 * faces too small to read an expression on, which in this product is the whole
 * content of the call. So a narrow screen starts where a phone call app would:
 * the other person filling the screen, you in the corner.
 *
 * Only the *starting point* differs. The picker is the same three options on
 * every screen, and a choice made on a phone is remembered like any other.
 */
export function defaultMeetingLayout(compact: boolean): MeetingLayoutId {
    return compact ? "speaker" : "gallery";
}

/**
 * Which layout is actually in force.
 *
 * Sharing a screen is an act of "look at this", and the person on the other
 * end should not have to find a menu before they can read what is being
 * pointed at. So while a screen is up the picture follows it by itself,
 * whatever this person normally watches calls in.
 *
 * Two things survive the override. `solo` stays `solo`, because it means "do
 * not show me my own face" — a preference about them, not about the stage.
 * And a layout pressed while the share is running is `pinned`, so the way
 * back to the gallery is one press and it holds until the share ends.
 *
 * Kept apart from `planStage` on purpose: that function says what a layout
 * looks like, this one says which layout it is. Folded together, an explicit
 * press on the gallery during a share could not produce a gallery.
 */
export function focusedLayout(
    chosen: MeetingLayoutId,
    { sharing, pinned }: { sharing: boolean; pinned: boolean }
): MeetingLayoutId {
    if (!sharing || pinned) return chosen;
    return chosen === "solo" ? "solo" : "speaker";
}

/**
 * Work out what goes where.
 *
 * The gallery is everything at equal size, a shared screen included. It is no
 * longer where a call sits through a share by accident — `focusedLayout`
 * moves off it — so reaching it now means someone asked for it.
 *
 * Elsewhere one thing holds the stage and everything else becomes a rail of
 * small tiles over it: the conversation does not stop because a document went
 * up, and in a couple session the partner who is listening does not leave the
 * screen because the other one is talking.
 *
 * What holds the stage, in order:
 *
 *   1. Whatever somebody pinned. An explicit press outranks every rule below
 *      it — including a share, because "I want to keep watching this face
 *      while you scroll" is a thing a therapist says out loud.
 *   2. A shared screen, theirs before yours: your own screen is already in
 *      front of you, and the one you cannot otherwise see is the one they are
 *      sharing.
 *   3. Whoever is talking. With two people this is the same answer the old
 *      rule gave; with three it is the difference between a couple session
 *      that follows the conversation and one stuck on whoever joined first.
 *   4. Failing that, the first other person, then — alone in the room —
 *      yourself.
 *
 * Alone in the room you are the stage, with no rail: a small tile of yourself
 * over a stage of yourself would be two of you.
 */
export function planStage<T extends StageTrack>(
    layout: MeetingLayoutId,
    tracks: T[]
): StagePlan<T> {
    if (layout === "gallery") return { grid: tracks, stage: null, rail: [] };

    const shares = tracks.filter((track) => track.isScreenShare);
    const share = shares.find((track) => !track.isLocal) ?? shares[0] ?? null;
    const mine = tracks.find((track) => track.isLocal && !track.isScreenShare) ?? null;
    const faces = tracks.filter((track) => !track.isLocal && !track.isScreenShare);
    const talking = faces.find((track) => track.isSpeaking) ?? null;
    const pinned = tracks.find((track) => track.isPinned) ?? null;

    const stage = pinned ?? share ?? talking ?? faces[0] ?? mine;
    if (!stage) return { grid: tracks, stage: null, rail: [] };

    // Everyone who is not on the stage, in the order the room published them
    // — including the other faces behind a share or a speaker, which the
    // single corner tile this replaces left off the screen entirely.
    //
    // `solo` still means "do not show me my own face": it is a preference
    // about this person, and neither a share nor somebody else talking is a
    // reason to hand it back.
    const rail = tracks.filter(
        (track) => track !== stage && !(layout === "solo" && track === mine)
    );
    return { grid: null, stage, rail };
}
